/*
* Copyright Gregory Coburn 2020-2026, All Rights Reserved, See license for further details
*/
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { StepperSelectionEvent } from '@angular/cdk/stepper';
import { HttpParams } from '@angular/common/http';
import { afterNextRender, Component, inject, Injector, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { MatTooltipModule } from '@angular/material/tooltip';
import { first } from 'rxjs/operators';
import { uuid } from '@src/app/model/abstract-object';
import { Ballot, Question } from '@src/app/model/ballot';
import { ServiceRequest } from '@src/app/model/serviceRequest';
import { RequestService } from '@src/app/modules/crm/request.service';
import { BallotService } from '@src/app/modules/social/ballot-page/ballot.service';
import { ConfirmDialogService } from '@src/app/shared/dialogs/confirmDialog';
import { DialogBannerComponent } from '@src/app/shared/dialogs/dialog-banner-component';
import { PicklistField } from '@src/app/shared/field/PicklistField';
import { FieldSet, FieldSetComponent } from '@src/app/shared/form/field-set/field-set.component';
import { FormConfig } from '@src/app/shared/form/FormConfig';

export interface BallotStepperData {
    ballotConfig: FormConfig;
    makeQuestionConfig: () => FormConfig;
    templateQuestions: () => Question[];
    forceTeamId: uuid;
    forumId?: uuid;
    pinnedRequest?: ServiceRequest;
}

export interface AgendaRequest {
    request: ServiceRequest;
    included: boolean;
    pinned: boolean;
}

type MeetingTimes = Pick<Question, 'opensAt' | 'closesAt'>;

@Component({
    selector: 'app-ballot-stepper',
    templateUrl: './ballot-stepper.component.html',
    styleUrl: './ballot-stepper.component.scss',
    imports: [DatePipe, NgTemplateOutlet, FormsModule, MatButtonModule, MatCheckboxModule, MatIconModule,
        MatStepperModule, MatTooltipModule, DialogBannerComponent, FieldSetComponent],
})
export class BallotStepperComponent {
    dialogRef = inject<MatDialogRef<BallotStepperComponent>>(MatDialogRef);
    private data = inject<BallotStepperData>(MAT_DIALOG_DATA);
    private ballotSvc = inject(BallotService);
    private injector = inject(Injector);
    private cds = inject(ConfirmDialogService);
    private requestSvc = inject(RequestService);

    @ViewChild('stepper') stepper: MatStepper;

    ballotFS = this.data.ballotConfig.fieldSet;
    questionSteps: FieldSet[] = [];
    summary: Ballot;
    channelName = '';
    sendNotifications = false;
    agendaRequests: AgendaRequest[] = [];
    private agendaForumId: uuid;
    saving = false;
    ready = false;

    private previousMeetingTimes: MeetingTimes = { opensAt: null, closesAt: null };

    constructor() {
        this.dialogRef.disableClose = true;
        this.ballotFS.getFormGroup();
        this.data.ballotConfig.objectFactory(null).pipe(first()).subscribe((ballot: Ballot) => {
            ballot.forumId = this.data.forumId ?? ballot.forumId;
            this.ballotFS.setValue(ballot);
            this.data.templateQuestions().forEach(q => this.questionSteps.push(this.makeQuestionStep(q)));
            this.ready = true;
        });
    }

    stepChanged(event: StepperSelectionEvent) {
        if (event.previouslySelectedIndex === 0) {
            this.copyMeetingTimesToQuestions();
        }
        if (event.selectedIndex === this.questionSteps.length + 1) {
            this.refreshSummary();
        }
    }

    addQuestion() {
        const question = new Question();
        Object.assign(question, this.meetingTimes());
        this.questionSteps.push(this.makeQuestionStep(question));
        this.selectStepAfterRender(this.questionSteps.length);
    }

    removeQuestion(questionStep: FieldSet, questionNr: number) {
        const title = this.questionLabel(questionStep, questionNr);
        this.cds.open($localize`Remove step`, $localize`Remove step "${title}" from the ballot?`, () => {
            this.questionSteps.splice(this.questionSteps.indexOf(questionStep), 1);
            this.refreshSummary();
            this.selectStepAfterRender(this.questionSteps.length + 1);
        });
    }

    finish() {
        const ballot = this.ballotFS.getFormValue() as Ballot;
        ballot.questions = this.questionSteps.map(fs => fs.getFormValue() as Question);
        ballot.requestIds = this.agendaRequests.filter(a => a.included).map(a => a.request.id);
        this.saving = true;
        this.ballotSvc.post(ballot, true, this.data.forceTeamId).pipe(first()).subscribe(saved => {
            this.saving = false;
            if (saved?.id) {
                if (this.sendNotifications) {
                    this.ballotSvc.notify(saved, this.data.forceTeamId);
                }
                this.dialogRef.close(saved);
            }
        });
    }

    questionLabel(questionFS: FieldSet, questionNr: number) {
        return questionFS.getFieldByName('question').control.value || $localize`Question ${questionNr + 1}`;
    }

    private makeQuestionStep(question: Question) {
        const questionFS = this.data.makeQuestionConfig().fieldSet;
        questionFS.setValue(question);
        return questionFS;
    }

    private meetingTimes(): MeetingTimes {
        return {
            opensAt: this.ballotFS.getFieldByName('opensAt').getFormValue(),
            closesAt: this.ballotFS.getFieldByName('closesAt').getFormValue(),
        };
    }

    private copyMeetingTimesToQuestions() {
        const meetingTimes = this.meetingTimes();
        for (const questionFS of this.questionSteps) {
            for (const timeName of ['opensAt', 'closesAt']) {
                const timeField = questionFS.getFieldByName(timeName);
                const questionTime = timeField.getFormValue();
                if (!questionTime || questionTime === this.previousMeetingTimes[timeName]) {
                    timeField.setValue(meetingTimes as Question, false);
                }
            }
        }
        this.previousMeetingTimes = meetingTimes;
    }

    private refreshSummary() {
        this.summary = this.ballotFS.getFormValue() as Ballot;
        this.summary.questions = this.questionSteps.map(fs => fs.getFormValue() as Question);
        const forumField = this.ballotFS.getFieldByName('forumId') as PicklistField;
        this.channelName = forumField.picklist.items.find(f => f.id === this.summary.forumId)?.['name'] ?? '';
        if (this.summary.forumId !== this.agendaForumId) {
            this.loadAgendaRequests(this.summary.forumId);
        }
    }

    /** Every request still awaiting acceptance in the channel is carried forward, unless unticked */
    private loadAgendaRequests(forumId: uuid) {
        this.agendaForumId = forumId;
        this.agendaRequests = this.pinnedAgendaRequests();
        const params = new HttpParams().set('forumId', forumId)
            .set('srStatus', `${ServiceRequest.srOpen.id},${ServiceRequest.srResolved.id}`);
        this.requestSvc.get<ServiceRequest>(false, params, this.data.forceTeamId).pipe(first()).subscribe(requests => {
            const carriedForward = requests
                .filter(request => request.id !== this.data.pinnedRequest?.id)
                .map(request => ({ request, included: true, pinned: false }));
            this.agendaRequests = [...this.pinnedAgendaRequests(), ...carriedForward];
        });
    }

    private pinnedAgendaRequests(): AgendaRequest[] {
        const pinned = this.data.pinnedRequest;
        return pinned ? [{ request: pinned, included: true, pinned: true }] : [];
    }

    private selectStepAfterRender(index: number) {
        afterNextRender(() => this.stepper.selectedIndex = index, { injector: this.injector });
    }
}
